import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar, { MobileSidebarClose } from './Sidebar';
import Logo from '../Logo';
import { Bell, List, SignOut, Star, Clock, CreditCard, Crown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AppLayout wraps every protected page with a persistent left Sidebar and a
 * compact top Header. Applies the warm Notion/Airbnb-like palette via
 * `.app-warm` class. Mobile: sidebar becomes a slide-in drawer via the
 * hamburger icon.
 */
export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Fetch unread notifications count
  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/notifications`, { withCredentials: true });
        if (!cancelled) setUnreadCount((res.data || []).filter((n) => !n.read).length);
      } catch { /* silent */ }
    };
    fetch();
    const t = setInterval(fetch, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const sub = user?.subscription || {};

  return (
    <div className="app-warm flex min-h-screen" data-testid="app-layout">
      {/* Desktop sidebar (fixed) */}
      <div className="hidden lg:block fixed inset-y-0 left-0 z-30">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 bg-zinc-900/40 z-40"
              onClick={() => setDrawerOpen(false)}
              data-testid="sidebar-backdrop"
            />
            <motion.div
              initial={{ x: -260 }}
              animate={{ x: 0 }}
              exit={{ x: -260 }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              className="lg:hidden fixed inset-y-0 left-0 z-50 shadow-2xl"
            >
              <div className="relative h-full">
                <MobileSidebarClose onClose={() => setDrawerOpen(false)} />
                <Sidebar onNavigate={() => setDrawerOpen(false)} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main column */}
      <div className="flex flex-col flex-1 lg:pl-[248px] min-w-0">
        {/* Top header */}
        <header className="sticky top-0 z-20 bg-[#FEFDFB]/90 backdrop-blur-md border-b border-[#EEEAE0]">
          <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            {/* Left: hamburger + mobile logo */}
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-zinc-100 flex-shrink-0"
                aria-label="Apri menu"
                data-testid="hamburger-btn"
              >
                <List size={22} weight="bold" />
              </button>
              <div className="lg:hidden">
                <Logo size="md" />
              </div>
            </div>

            {/* Right: subscription + notifications + logout */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wide transition-colors"
                  data-testid="header-admin-btn"
                >
                  <Crown size={14} weight="fill" /> Admin
                </button>
              )}
              <button
                onClick={() => navigate('/subscription')}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAF7F2] hover:bg-[#EEEAE0] transition-colors text-xs font-semibold text-zinc-700"
                data-testid="header-subscription-btn"
              >
                {sub.plan_type === 'lifetime' && (<><Crown size={14} weight="fill" className="text-emerald-600" /> A Vita</>)}
                {sub.plan_type === 'yearly' && (<><Star size={14} weight="fill" className="text-amber-500" /> Annuale</>)}
                {sub.plan_type === 'quarterly' && (<><Star size={14} weight="fill" className="text-purple-500" /> Trimestrale</>)}
                {sub.plan_type === 'monthly' && (<><CreditCard size={14} className="text-blue-500" /> Mensile</>)}
                {sub.plan_type === 'trial' && (<><Clock size={14} className="text-amber-500" /> {sub.days_remaining}gg trial</>)}
                {sub.plan_type === 'expired' && (<><CreditCard size={14} className="text-red-500" /> Scaduto</>)}
                {sub.plan_type === 'admin' && (<><Crown size={14} weight="fill" className="text-amber-500" /> Admin</>)}
              </button>
              <button
                onClick={() => navigate('/notifications')}
                className="relative p-2 rounded-full hover:bg-zinc-100 transition-colors"
                aria-label="Notifiche"
                data-testid="header-notifications-btn"
              >
                <Bell size={20} weight="regular" className="text-zinc-700" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#0d9488] text-white text-[10px] font-bold flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={logout}
                className="p-2 rounded-full hover:bg-zinc-100 transition-colors"
                aria-label="Esci"
                data-testid="header-logout-btn"
              >
                <SignOut size={20} className="text-zinc-600" />
              </button>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
